import { Notice } from '@shared/ui/Notice'
import { RecoveryDisplay } from '@shared/ui/RecoveryDisplay'
import { useRotateRecoveryKeyForm } from '@features/recovery-key/hooks/useRotateRecoveryKeyForm'
import { useTranslation } from '@/i18n'

/**
 * Recovery Key 再生成セクション (Track E-2)。
 *
 * - `idle`: 警告 + 「再生成する」button のみ。MP 入力なし (SK が unlocked にあれば十分)
 * - `submitting`: button を無効化、文言を「再生成中…」に
 * - `revealed`: 共有 `<RecoveryDisplay>` で新 24 単語を一回限り表示、確認チェック後 close
 * - `error`: danger Notice + 再試行可能
 *
 * 親 (`SettingsPage`) は `onClose` を渡す。`revealed` の確認完了でこれを呼ぶ。
 * フォーム状態の折りたたみはセクションヘッダのトグルで行う (重複する
 * 「閉じる」ボタンは本セクション内に置かない)。
 */

export interface RotateRecoveryKeySectionProps {
  onClose: () => void
}

export const RotateRecoveryKeySection = ({
  onClose,
}: RotateRecoveryKeySectionProps): JSX.Element => {
  const { t } = useTranslation()
  const { state, isSubmitting, start, reset } = useRotateRecoveryKeyForm()

  if (state.kind === 'revealed') {
    return (
      <div className="card settings-security-item" data-testid="rotate-recovery-revealed">
        <RecoveryDisplay
          mnemonic={state.mnemonic}
          onProceed={() => {
            reset()
            onClose()
          }}
          title={t('settings.security.rotate_recovery_key_success_intro')}
          warning={t('settings.security.rotate_recovery_key_mnemonic_intro')}
          copyLabel={t('auth.register.recovery_copy')}
          copiedLabel={t('auth.register.recovery_copied')}
          confirmLabel={t('settings.security.rotate_recovery_key_confirm_saved')}
          proceedLabel={t('settings.security.rotate_recovery_key_close')}
        />
      </div>
    )
  }

  return (
    <div className="card settings-security-item" data-testid="rotate-recovery-form">
      <div className="settings-card-title">{t('settings.security.rotate_recovery_key')}</div>
      <Notice tone="warn" ico="alert">
        {t('settings.security.rotate_recovery_key_warning')}
      </Notice>

      {state.kind === 'error' && (
        <Notice tone="danger" ico="alert">
          {t(state.messageKey)}
        </Notice>
      )}

      <div className="settings-form-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={isSubmitting}
          onClick={() => void start()}
        >
          {isSubmitting
            ? t('settings.security.rotate_recovery_key_submitting')
            : t('settings.security.rotate_recovery_key_submit')}
        </button>
      </div>
    </div>
  )
}
